import type { DxTradeCashTransfer } from '@model/DxTrade.types'
import { DxTradeCashTransactionTypes } from '@model/DxTrade.types'

export type DxtradeCashTransferType =
  (typeof DxTradeCashTransactionTypes)[keyof typeof DxTradeCashTransactionTypes]

export interface DxtradeCashTransfersQuery {
  accountCodes?: string
  limit?: number
  startFrom?: number
  ofType?: DxtradeCashTransferType[]
  timeFrom?: string
  timeTo?: string
  period?: 'today' | 'previous-day' | 'week' | 'previous-week'
  includeOffset?: boolean
}

export function prepareDxtradeCashTransfersParams(
  query?: DxtradeCashTransfersQuery
): URLSearchParams {
  const params = new URLSearchParams()
  if (!query) return params

  if (query.accountCodes) params.set('account-codes', query.accountCodes)
  if (query.limit != null) params.set('limit', String(query.limit))
  if (query.startFrom != null) params.set('start-from', String(query.startFrom))
  if (query.ofType?.length) params.set('of-type', query.ofType.join(','))
  if (query.timeFrom) params.set('time-from', query.timeFrom)
  if (query.timeTo) params.set('time-to', query.timeTo)
  if (query.period) params.set('period', query.period)
  if (query.includeOffset != null) params.set('include-offset', String(query.includeOffset))

  return params
}

export const DEFAULT_CASH_TRANSFERS_QUERY: DxtradeCashTransfersQuery = {
  ofType: [DxTradeCashTransactionTypes.FINANCING],
}

export interface OvernightFees {
  total: number
}

export type OvernightFeesMap = Map<string, OvernightFees>

export interface ParsedFinancingTransfer {
  total: number
  positionNumbers: string[]
}

function sumFinancing(transfer: DxTradeCashTransfer): number {
  let total = 0
  for (const tx of transfer.cashTransactions ?? []) {
    if (tx.type !== DxTradeCashTransactionTypes.FINANCING) continue
    total += tx.value ?? 0
  }
  return total
}

export function parseFinancingTransfers(
  cashTransfers: DxTradeCashTransfer[] | null | undefined
): ParsedFinancingTransfer[] {
  if (!cashTransfers?.length) return []

  const result: ParsedFinancingTransfer[] = []

  for (const transfer of cashTransfers) {
    const total = sumFinancing(transfer)
    if (!total) continue

    const positionNumbers = transfer.comment?.match(/\d+/g)
    if (!positionNumbers?.length) continue

    result.push({ total, positionNumbers })
  }

  return result
}

export function buildOvernightFeesMap(
  parsedTransfers: ParsedFinancingTransfer[],
  positionIds: Iterable<string>
): OvernightFeesMap {
  const map: OvernightFeesMap = new Map()

  const ids = new Set(positionIds)
  if (!parsedTransfers.length || !ids.size) return map

  for (const { total, positionNumbers } of parsedTransfers) {
    const positionId = positionNumbers.find((number) => ids.has(number))
    if (!positionId) continue

    map.set(positionId, { total: (map.get(positionId)?.total ?? 0) + total })
  }

  return map
}

export function sumOvernightFeesForPosition(
  cashTransfers: DxTradeCashTransfer[] | null | undefined,
  positionCode: string
): OvernightFees | null {
  if (!cashTransfers?.length || !positionCode) return null

  let total = 0
  let hasFinancing = false

  for (const transfer of cashTransfers) {
    const financing = sumFinancing(transfer)
    if (!financing) continue

    if (!transfer.comment?.match(/\d+/g)?.includes(positionCode)) continue

    total += financing
    hasFinancing = true
  }

  return hasFinancing ? { total } : null
}
